import * as React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge, BadgeVariants } from "~/components/Badge";
import {
    ColumnMetadata,
    ColumnTypes,
    DataGrid,
    RowSizes,
} from "~/components/DataGrid";
import { DataGridPercentValue } from "~/components/DataGrid/DataGridPercentValue";
import { ImageLoader } from "~/components/ImageLoader";
import { Link } from "~/components/Link";
import { Emdash } from "~/components/Punctuation";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { MultiSelect, MultiSelectOption } from "~/components/MultiSelect";
import { RWA_COIN_ICON } from "~/consts";
import { AssetTableRow } from "~/modules/assets/components/AssetTable/AssetTableRow";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { FilterLabel } from "~/modules/asset_screener/pages/listing/FilterLabel";
import { ASSET_CLASS_COLUMN } from "~/modules/assets/components/AssetTable/columns/AssetClass";
import { INCEPTION_DATE_COLUMN } from "~/modules/assets/components/AssetTable/columns/InceptionDate";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN } from "~/modules/assets/components/AssetTable/columns/PrimaryMarket";
import { ASSET_SORT_MAP } from "~/modules/assets/components/AssetTable/useInMemoryAssetSort";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    PaginationState,
    SortDirections,
} from "~/modules/filters/types";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import * as Analytics from "~/modules/new_asset_monitor/analytics";
import {
    ChartTypes,
    TimeSeriesUnits,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import { cn } from "~/lib/utils";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { getAssetURL } from "~/utils/getAssetURL";
import { formatApy } from "~/utils/functions/formatApy";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { uniqueValues } from "~/utils/functions/uniqueValues";
import { RWA_COLORS } from "~/styles/colors";
import { SelectLoginGate } from "~/components/SelectInput/SelectLoginGate";
import { useUser } from "~/modules/user/hooks/useUser";

// // // //

type IssueCountGroupingValue =
    "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
const DEFAULT_TABLE_GROUPING: IssueCountGroupingValue = "weekly";
const DEFAULT_CHART_GROUPING: IssueCountGroupingValue = "monthly";
const PAGE_SIZE = 25;
const UNKNOWN_REGISTRATION_GROUP_KEY = "__unknown_registration_date__";
const FALLBACK_ASSET_CLASS_CHART_COLORS = [
    RWA_COLORS.blue[500],
    RWA_COLORS.emerald[500],
    RWA_COLORS.amber[500],
    RWA_COLORS.indigo[500],
    RWA_COLORS.teal[500],
    RWA_COLORS.orange[500],
    RWA_COLORS.cyan[500],
    RWA_COLORS.green[500],
    RWA_COLORS.slate[500],
];

const GROUP_DAY_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
});

const GROUP_WEEK_FORMATTER = GROUP_DAY_FORMATTER;

const GROUP_MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
});

const ISSUE_COUNT_GROUPING_OPTIONS: Array<{
    value: IssueCountGroupingValue;
    label: string;
}> = [
    {
        value: "daily",
        label: "Day",
    },
    {
        value: "weekly",
        label: "Week",
    },
    {
        value: "monthly",
        label: "Month",
    },
    {
        value: "quarterly",
        label: "Quarter",
    },
    {
        value: "yearly",
        label: "Year",
    },
];

const CONTROL_INPUT_CLASS_NAME =
    "h-9 rounded-md bg-card px-3 py-1.5 text-sm shadow-xs md:text-sm disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:placeholder:text-slate-500 disabled:opacity-100";

const CONTROL_SELECT_TRIGGER_CLASS_NAME =
    "w-full min-w-0 h-9 rounded-md border border-input bg-card px-3 py-1.5 text-sm text-foreground shadow-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-100 disabled:hover:bg-slate-100 select-none";

const TABLE_SORT_MAP: Record<string, string> = {
    ...ASSET_SORT_MAP,
    [AssetColumns._created_at]: "_created_at",
    [AssetColumns.inceptionDate]: "inception_date",
    [AssetColumns.primaryMarketMinimumSubscriptionAmount]:
        "primary_market.subscription_minimum_amount",
    [AssetColumns.protocolNames]: "tokens.0.platform.name",
    [AssetColumns.isInvestable]: "is_investable",
};

const DEFAULT_TABLE_QUERY: ApiQuery = {
    sort: {
        field: AssetColumns._created_at,
        direction: SortDirections.desc,
    },
    pagination: {
        page: 1,
        perPage: PAGE_SIZE,
    },
};

const ISSUANCE_DATE_COLUMN_WIDTH = 140;
const REGISTRATION_DATE_COLUMN_WIDTH = 130;
const ASSET_COLUMN_WIDTH = 350;

const REGISTRATION_DATE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns._created_at,
    label: (
        <span className="flex flex-col items-end text-right leading-tight whitespace-normal">
            <span>Registration</span>
            <span>Date</span>
        </span>
    ),
    type: ColumnTypes.date,
    width: REGISTRATION_DATE_COLUMN_WIDTH,
    sortsOn: AssetColumns._created_at,
    loginRequired: true,
    cellValue: (item) => {
        if (item.isAsset) {
            return item._created_at ?? null;
        }

        return null;
    },
};

const ISSUANCE_DATE_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    ...INCEPTION_DATE_COLUMN,
    label: "Issuance Date",
    width: ISSUANCE_DATE_COLUMN_WIDTH,
    sortsOn: AssetColumns.inceptionDate,
    loginRequired: true,
};

const ASSET_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    slug: AssetColumns.name,
    label: "Asset Name",
    type: ColumnTypes.string,
    width: ASSET_COLUMN_WIDTH,
    sortsOn: AssetColumns.name,
    loginRequired: true,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        return (
            <Link
                href={getAssetURL({ asset: item })}
                variant="default"
                className="select-none pl-3 flex items-center gap-2"
                analyticsLabel={AssetUserEvents.ClickIndividualLink}
            >
                <ImageLoader
                    src={item.icon_url ?? RWA_COIN_ICON}
                    alt={`${item.name} Icon`}
                    fallbackSrc={RWA_COIN_ICON}
                    className="shrink-0 rounded-full h-5 w-5 min-w-5"
                />
                <span className="dg-overflow">{item.name}</span>
            </Link>
        );
    },
};

const MONITOR_ASSET_CLASS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    ...ASSET_CLASS_COLUMN,
    // proPlanRequired: true,
    width: 170,
    sortsOn: undefined,
};

const MONITOR_PLATFORM_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> =
    {
        ...PLATFORM_COLUMN,
        loginRequired: true,
        width: 180,
        sortsOn: AssetColumns.protocolNames,
    };

const CURRENT_ASSET_VALUE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.totalAssetValueDollar,
    label: (
        <span className="flex flex-col items-end text-right leading-tight whitespace-normal">
            <span>Net Flows</span>
            <span className="text-xs">(since launch)</span>
        </span>
    ),
    type: ColumnTypes.dollar,
    width: 180,
    // proPlanRequired: true,
    sortsOn: undefined,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        const value = getMeasureValue(item, "total_asset_value_dollar")?.val;

        if (typeof value !== "number") {
            return <Emdash />;
        }

        return (
            <span className="font-medium tabular-nums text-emerald-600">
                +${formatCurrencyValue(Math.abs(value))}
            </span>
        );
    },
};

const MIN_INVESTMENT_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    ...PRIMARY_MARKET_MINIMUM_SUBSCRIPTION_AMT_COLUMN,
    width: 165,
    loginRequired: true,
};

const ELIGIBLE_INVESTORS_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.primaryMarketInvestorTypes,
    label: "Eligible Investors",
    sortsOn: AssetColumns.primaryMarketInvestorTypes,
    type: ColumnTypes.string,
    width: 170,
    loginRequired: true,
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        const eligibleInvestorTypes = getEligibleInvestorTypes(item);

        if (eligibleInvestorTypes.length === 0) {
            return <Emdash />;
        }

        return (
            <div className="w-full overflow-hidden">
                <div className="flex flex-nowrap items-center gap-1 overflow-hidden">
                    {eligibleInvestorTypes.map((value) => (
                        <Badge
                            key={value}
                            size="sm"
                            rounded="sm"
                            className="shrink-0 whitespace-nowrap cursor-default"
                            label={value}
                            variant={BadgeVariants.gray}
                        />
                    ))}
                </div>
            </div>
        );
    },
};

const INDICATIVE_YIELD_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> =
    {
        slug: "indicative_yield" as AssetColumns,
        label: "Indicative Yield",
        tooltip: "Uses yield to maturity when available, otherwise 30D APY.",
        type: ColumnTypes.percent,
        width: 135,
        loginRequired: true,
        render: (item) => {
            if (!item.isAsset) {
                return <Emdash />;
            }

            const indicativeYield = getIndicativeYield(item);

            if (indicativeYield === null) {
                return <Emdash />;
            }

            return <DataGridPercentValue value={indicativeYield.value} />;
        },
    };

const TABLE_COLUMNS: ColumnMetadata<AssetTableRowData, AssetColumns>[] = [
    ASSET_COLUMN,
    MONITOR_ASSET_CLASS_COLUMN,
    REGISTRATION_DATE_COLUMN,
    CURRENT_ASSET_VALUE_COLUMN,
    MONITOR_PLATFORM_COLUMN,
    MIN_INVESTMENT_COLUMN,
    ELIGIBLE_INVESTORS_COLUMN,
    INDICATIVE_YIELD_COLUMN,
    ISSUANCE_DATE_COLUMN,
];

const TABLE_MIN_WIDTH = TABLE_COLUMNS.reduce(
    (totalWidth, column) => totalWidth + column.width,
    0,
);

// // // //

function normalizeDate(value: string | Date | null | undefined) {
    if (!value) {
        return null;
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
        return null;
    }

    return dateValue;
}

function getIndicativeYield(asset: Asset) {
    const yieldToMaturity = asset.yield_to_maturity_percent?.val;

    if (typeof yieldToMaturity === "number") {
        return {
            value: yieldToMaturity,
            label: "YTM",
        };
    }

    const apy30Day = getMeasureValue(asset, "apy_30_day");

    if (apy30Day && typeof apy30Day.val === "number") {
        return {
            value: apy30Day.val / 100,
            label: "30D APY",
        };
    }

    return null;
}

function getEligibleInvestorTypes(asset: Asset) {
    if (!asset.primary_markets || asset.primary_markets.length === 0) {
        return [];
    }

    return uniqueValues(
        asset.primary_markets.reduce<string[]>((acc, market) => {
            const values = market.allowed_investor_types || [];
            return [...acc, ...values];
        }, []),
    ).filter((value) => value.trim() !== "");
}

function getUtcStartOfDay(date: Date) {
    return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );
}

function getUtcStartOfWeek(date: Date) {
    const startOfDay = getUtcStartOfDay(date);
    const daysSinceMonday = (startOfDay.getUTCDay() + 6) % 7;

    startOfDay.setUTCDate(startOfDay.getUTCDate() - daysSinceMonday);

    return startOfDay;
}

function getUtcStartOfMonth(date: Date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function getUtcStartOfQuarter(date: Date) {
    return new Date(
        Date.UTC(
            date.getUTCFullYear(),
            Math.floor(date.getUTCMonth() / 3) * 3,
            1,
        ),
    );
}

function getUtcStartOfYear(date: Date) {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}

function formatRegistrationGroupLabel(
    date: Date,
    grouping: IssueCountGroupingValue,
) {
    if (grouping === "daily") {
        return GROUP_DAY_FORMATTER.format(date);
    }

    if (grouping === "weekly") {
        return `Week of ${GROUP_WEEK_FORMATTER.format(date)}`;
    }

    if (grouping === "monthly") {
        return GROUP_MONTH_FORMATTER.format(date);
    }

    if (grouping === "quarterly") {
        return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
    }

    if (grouping === "yearly") {
        return `${date.getUTCFullYear()}`;
    }

    return GROUP_MONTH_FORMATTER.format(date);
}

function getRegistrationGroup(asset: Asset, grouping: IssueCountGroupingValue) {
    const registrationDate = normalizeDate(asset._created_at);

    if (!registrationDate) {
        return {
            key: `${UNKNOWN_REGISTRATION_GROUP_KEY}-${grouping}`,
            label: "Registration Date Unavailable",
            timestamp: null,
        };
    }

    const groupStartDate =
        grouping === "daily"
            ? getUtcStartOfDay(registrationDate)
            : grouping === "weekly"
              ? getUtcStartOfWeek(registrationDate)
              : grouping === "monthly"
                ? getUtcStartOfMonth(registrationDate)
                : grouping === "quarterly"
                  ? getUtcStartOfQuarter(registrationDate)
                  : getUtcStartOfYear(registrationDate);

    return {
        key: `${grouping}-${groupStartDate.getUTCFullYear()}-${String(
            groupStartDate.getUTCMonth() + 1,
        ).padStart(2, "0")}-${String(groupStartDate.getUTCDate()).padStart(
            2,
            "0",
        )}`,
        label: formatRegistrationGroupLabel(groupStartDate, grouping),
        timestamp: groupStartDate.getTime(),
    };
}

function buildIssueCountSeries(
    assets: Asset[],
    grouping: IssueCountGroupingValue,
): TimeSeriesView[] {
    const seriesByAssetClass = new Map<
        string,
        {
            id: number;
            name: string;
            color: string;
            counts: Map<number, number>;
        }
    >();

    assets.forEach((asset) => {
        const registrationGroup = getRegistrationGroup(asset, grouping);

        if (registrationGroup.timestamp === null) {
            return;
        }

        const assetClassKey =
            asset.asset_class?.slug ??
            asset.asset_class_name ??
            `unknown-${asset.asset_class_id ?? 0}`;
        const assetClassName =
            asset.asset_class?.name ??
            asset.asset_class_name ??
            "Unknown Asset Class";

        const existingSeries = seriesByAssetClass.get(assetClassKey);

        if (existingSeries) {
            existingSeries.counts.set(
                registrationGroup.timestamp,
                (existingSeries.counts.get(registrationGroup.timestamp) ?? 0) +
                    1,
            );
            return;
        }

        const nextSeriesIndex = seriesByAssetClass.size;

        seriesByAssetClass.set(assetClassKey, {
            id:
                asset.asset_class?.id ??
                asset.asset_class_id ??
                nextSeriesIndex,
            name: assetClassName,
            color:
                asset.asset_class?.color_hex ??
                FALLBACK_ASSET_CLASS_CHART_COLORS[
                    nextSeriesIndex % FALLBACK_ASSET_CLASS_CHART_COLORS.length
                ],
            counts: new Map([[registrationGroup.timestamp, 1]]),
        });
    });

    return Array.from(seriesByAssetClass.values())
        .map((series) => ({
            ...series,
            total: Array.from(series.counts.values()).reduce(
                (sum, count) => sum + count,
                0,
            ),
        }))
        .sort((a, b) => {
            if (b.total !== a.total) {
                return b.total - a.total;
            }
            return a.name.localeCompare(b.name);
        })
        .map((series) => ({
            enabled: true,
            chartType: ChartTypes.column,
            item: {
                measure: {
                    id: 1,
                    slug: "issue_count",
                    name: "Issue Count",
                    unit: TimeSeriesUnits.address,
                },
                group: {
                    id: series.id,
                    type: "asset_class",
                    name: series.name,
                    slug: series.name,
                    color: series.color,
                },
                points: Array.from(series.counts.entries())
                    .sort((a, b) => a[0] - b[0])
                    .map(([timestamp, count]) => [
                        new Date(timestamp).toISOString(),
                        count,
                    ]),
            },
        }));
}

function getSearchDateTerms(value: string | Date | null | undefined) {
    const date = normalizeDate(value);

    if (!date) {
        return [];
    }

    return [
        date.toISOString(),
        date.toISOString().split("T")[0] ?? "",
        date.toLocaleDateString("en-US", {
            timeZone: "UTC",
        }),
        GROUP_DAY_FORMATTER.format(date),
    ];
}

function getAssetSearchText(asset: Asset) {
    const currentAssetValue = getMeasureValue(
        asset,
        "total_asset_value_dollar",
    )?.val;
    const indicativeYield = getIndicativeYield(asset);
    const minInvestment =
        asset.primary_market?.subscription_minimum_amount ?? null;
    const minInvestmentCurrency =
        asset.primary_market?.base_asset_ticker ?? null;

    const searchTerms = [
        asset.name,
        asset.ticker,
        asset.asset_class?.name,
        asset.asset_class?.slug,
        asset.asset_class_name,
        asset.manager?.name,
        asset.issuer?.name,
        asset.issuer_name,
        ...asset.tokens.flatMap((token) => [
            token.platform?.name,
            token.platform?.slug,
            token.asset_manager?.name,
        ]),
        ...getSearchDateTerms(asset.inception_date),
        ...getSearchDateTerms(asset._created_at),
        ...getEligibleInvestorTypes(asset),
        indicativeYield?.label,
        indicativeYield ? `${indicativeYield.value}` : null,
        indicativeYield ? `${formatApy(indicativeYield.value)}` : null,
        indicativeYield ? `${formatApy(indicativeYield.value)}%` : null,
        typeof currentAssetValue === "number" ? `${currentAssetValue}` : null,
        typeof currentAssetValue === "number"
            ? currentAssetValue.toLocaleString("en-US")
            : null,
        typeof currentAssetValue === "number"
            ? `$${formatCurrencyValue(currentAssetValue)}`
            : null,
        typeof currentAssetValue === "number"
            ? `$${formatCurrencyValueCompact(currentAssetValue)}`
            : null,
        typeof minInvestment === "number" ? `${minInvestment}` : null,
        typeof minInvestment === "number"
            ? minInvestment.toLocaleString("en-US")
            : null,
        minInvestmentCurrency,
        typeof minInvestment === "number" && minInvestmentCurrency
            ? `${minInvestment} ${minInvestmentCurrency}`
            : null,
        typeof minInvestment === "number" && minInvestmentCurrency
            ? `${minInvestment.toLocaleString("en-US")} ${minInvestmentCurrency}`
            : null,
    ];

    return searchTerms
        .filter((value): value is string =>
            Boolean(value && value.trim() !== ""),
        )
        .join(" ")
        .toLowerCase();
}

function getAssetClassOptions(assets: Asset[]): MultiSelectOption[] {
    const counts: Record<string, number> = {};
    const icons: Record<
        string,
        ReturnType<typeof getAssetClassLinkMetadata>["icon"]
    > = {};
    const labels = new Map<string, string>();

    assets.forEach((asset) => {
        const key = String(asset.asset_class?.id ?? asset.asset_class_id ?? "");

        if (!key) {
            return;
        }

        counts[key] = (counts[key] || 0) + 1;
        labels.set(
            key,
            asset.asset_class?.name ??
                asset.asset_class_name ??
                "Unknown Asset Class",
        );

        if (!icons[key]) {
            icons[key] = getAssetClassLinkMetadata(
                asset.asset_class?.id ?? asset.asset_class_id ?? null,
            ).icon;
        }
    });

    return sortItemsByProperty({
        items: Array.from(labels.entries()).map(([value, label]) => ({
            value,
            label,
            count: counts[value] || 0,
            icon: icons[value],
        })),
        field: "count",
        direction: SortDirections.desc,
    });
}

function getPlatformOptions(assets: Asset[]): MultiSelectOption[] {
    const counts: Record<string, number> = {};
    const icons: Record<string, string | null> = {};

    assets.forEach((asset) => {
        const assetPlatformNames = uniqueValues(
            asset.tokens
                .map((token) => token.platform?.name)
                .filter((value): value is string => Boolean(value)),
        );

        assetPlatformNames.forEach((name) => {
            counts[name] = (counts[name] || 0) + 1;
        });

        asset.tokens.forEach((token) => {
            if (token.platform?.name && !icons[token.platform.name]) {
                icons[token.platform.name] = token.platform.icon_url || null;
            }
        });
    });

    return sortItemsByProperty({
        items: Object.keys(counts).map((platform) => ({
            value: platform,
            label: platform,
            count: counts[platform] || 0,
            icon: icons[platform] || undefined,
        })),
        field: "count",
        direction: SortDirections.desc,
    });
}

function getNetworkOptions(assets: Asset[]): MultiSelectOption[] {
    const counts: Record<string, number> = {};
    const icons: Record<string, string | null> = {};

    assets.forEach((asset) => {
        const assetNetworkNames = uniqueValues(
            asset.tokens
                .map((token) => token.network?.name)
                .filter((value): value is string => Boolean(value)),
        );

        assetNetworkNames.forEach((name) => {
            counts[name] = (counts[name] || 0) + 1;
        });

        asset.tokens.forEach((token) => {
            if (token.network?.name && !icons[token.network.name]) {
                icons[token.network.name] = token.network.icon_url || null;
            }
        });
    });

    return sortItemsByProperty({
        items: Object.keys(counts).map((network) => ({
            value: network,
            label: network,
            count: counts[network] || 0,
            icon: icons[network] || undefined,
        })),
        field: "count",
        direction: SortDirections.desc,
    });
}

function RegistrationGroupRow(props: {
    label: string;
    itemCount: number;
    collapsed: boolean;
    onToggle: () => void;
}) {
    const { label, itemCount, collapsed, onToggle } = props;

    return (
        <tr className="bg-transparent select-none">
            <td colSpan={TABLE_COLUMNS.length} className="p-0">
                <button
                    type="button"
                    aria-expanded={!collapsed}
                    onClick={onToggle}
                    style={{
                        minWidth: `${TABLE_MIN_WIDTH}px`,
                        backgroundImage: `linear-gradient(to bottom, ${RWA_COLORS.slate[200]} 0%, ${RWA_COLORS.slate[200]} 32%, rgba(218, 226, 240, 0.9) 78%, rgba(218, 226, 240, 0.9) 100%)`,
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 px-3 py-1.5 text-left text-sm font-medium text-slate-700 transition-[filter] hover:brightness-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                >
                    <span className="flex min-w-0 items-center gap-1.5">
                        {collapsed ? (
                            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        ) : (
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                        )}
                        <span className="truncate">{label}</span>
                        <span
                            aria-hidden="true"
                            className="h-1 w-1 shrink-0 rounded-full bg-slate-400"
                        />
                        <span className="shrink-0 text-xs font-normal text-slate-500">
                            {itemCount.toLocaleString()}{" "}
                            {itemCount === 1 ? "asset" : "assets"}
                        </span>
                    </span>
                </button>
            </td>
        </tr>
    );
}

// // // //

export function NewAssetMonitorListingPage(props: { assets: Asset[] }) {
    const { assets } = props;
    const [searchQuery, setSearchQuery] = React.useState("");
    const [selectedAssetClasses, setSelectedAssetClasses] = React.useState<
        string[]
    >([]);
    const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>(
        [],
    );
    const [selectedNetworks, setSelectedNetworks] = React.useState<string[]>(
        [],
    );
    const { isLoggedIn } = useUser();
    const [selectedTableGrouping, setSelectedTableGrouping] =
        React.useState<IssueCountGroupingValue>(DEFAULT_TABLE_GROUPING);
    const [selectedIssueCountGrouping, setSelectedIssueCountGrouping] =
        React.useState<IssueCountGroupingValue>(DEFAULT_CHART_GROUPING);
    const [showIssueCountLegend, setShowIssueCountLegend] =
        React.useState(true);
    const [collapsedRegistrationGroups, setCollapsedRegistrationGroups] =
        React.useState<Set<string>>(new Set());
    const [query, setQuery] = React.useState<ApiQuery>(DEFAULT_TABLE_QUERY);
    const issueCountChartContainerRef = React.useRef<HTMLDivElement | null>(
        null,
    );
    const [issueCountChartHeight, setIssueCountChartHeight] =
        React.useState(320);

    const deferredSearchQuery = React.useDeferredValue(searchQuery);
    const assetClassOptions = React.useMemo(
        () => getAssetClassOptions(assets),
        [assets],
    );

    const platformOptions = React.useMemo(
        () => getPlatformOptions(assets),
        [assets],
    );

    const networkOptions = React.useMemo(
        () => getNetworkOptions(assets),
        [assets],
    );

    const filteredAssets = React.useMemo(() => {
        const normalizedSearch = deferredSearchQuery.trim().toLowerCase();

        return assets.filter((asset) => {
            const assetClassId = String(
                asset.asset_class?.id ?? asset.asset_class_id ?? "",
            );

            if (
                selectedAssetClasses.length > 0 &&
                !selectedAssetClasses.includes(assetClassId)
            ) {
                return false;
            }

            if (
                selectedPlatforms.length > 0 &&
                !asset.tokens.some((token) =>
                    selectedPlatforms.includes(token.platform?.name || ""),
                )
            ) {
                return false;
            }

            if (
                selectedNetworks.length > 0 &&
                !asset.tokens.some((token) =>
                    selectedNetworks.includes(token.network?.name || ""),
                )
            ) {
                return false;
            }

            if (!normalizedSearch) {
                return true;
            }

            return getAssetSearchText(asset).includes(normalizedSearch);
        });
    }, [
        assets,
        deferredSearchQuery,
        selectedAssetClasses,
        selectedNetworks,
        selectedPlatforms,
    ]);

    const issueCountSeries = React.useMemo(() => {
        return buildIssueCountSeries(assets, selectedIssueCountGrouping);
    }, [assets, selectedIssueCountGrouping]);

    const issueCountGroupByTabOptions = React.useMemo(
        () =>
            ISSUE_COUNT_GROUPING_OPTIONS.filter(
                (option) =>
                    option.value !== "daily" && option.value !== "weekly",
            ).map((option) => ({
                label: option.label,
                value: option.value,
            })),
        [],
    );

    const tableGroupByOptions = React.useMemo(
        () =>
            ISSUE_COUNT_GROUPING_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
            })),
        [],
    );

    const sortedAssets = React.useMemo(() => {
        const sortField = TABLE_SORT_MAP[query.sort.field] ?? query.sort.field;

        return sortItemsByProperty({
            items: [...filteredAssets],
            field: sortField,
            direction: query.sort.direction,
            nullsLast: true,
            caseSensitive: false,
        });
    }, [filteredAssets, query.sort.direction, query.sort.field]);

    const pageCount = Math.max(
        1,
        Math.ceil(sortedAssets.length / query.pagination.perPage),
    );

    React.useEffect(() => {
        if (query.pagination.page <= pageCount) {
            return;
        }

        setQuery((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: pageCount,
            },
        }));
    }, [pageCount, query.pagination.page]);

    const paginationState: PaginationState = React.useMemo(
        () => ({
            page: query.pagination.page,
            perPage: query.pagination.perPage,
            pageCount,
            resultCount: sortedAssets.length,
        }),
        [
            pageCount,
            query.pagination.page,
            query.pagination.perPage,
            sortedAssets,
        ],
    );

    const paginatedAssets = React.useMemo(() => {
        const startIndex =
            (query.pagination.page - 1) * query.pagination.perPage;
        const endIndex = startIndex + query.pagination.perPage;
        return sortedAssets.slice(startIndex, endIndex);
    }, [query.pagination.page, query.pagination.perPage, sortedAssets]);

    const tableRegistrationGroupCounts = React.useMemo(() => {
        const counts = new Map<string, number>();

        sortedAssets.forEach((asset) => {
            const groupKey = getRegistrationGroup(
                asset,
                selectedTableGrouping,
            ).key;

            counts.set(groupKey, (counts.get(groupKey) ?? 0) + 1);
        });

        return counts;
    }, [selectedTableGrouping, sortedAssets]);

    const paginatedRegistrationGroups = React.useMemo(() => {
        const groups: Array<{ key: string; label: string }> = [];

        paginatedAssets.forEach((asset) => {
            const group = getRegistrationGroup(asset, selectedTableGrouping);
            const previousGroup = groups[groups.length - 1];

            if (previousGroup?.key === group.key) {
                return;
            }

            groups.push(group);
        });

        return groups;
    }, [paginatedAssets, selectedTableGrouping]);

    const activeFilterCount = [
        searchQuery.trim() !== "",
        selectedAssetClasses.length > 0,
        selectedPlatforms.length > 0,
        selectedNetworks.length > 0,
        selectedTableGrouping !== DEFAULT_TABLE_GROUPING,
    ].filter(Boolean).length;

    const resetToFirstPage = React.useCallback(() => {
        setQuery((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: 1,
            },
        }));
    }, []);

    const handleClearFilters = React.useCallback(() => {
        setSearchQuery("");
        setSelectedAssetClasses([]);
        setSelectedPlatforms([]);
        setSelectedNetworks([]);
        setSelectedTableGrouping(DEFAULT_TABLE_GROUPING);
        setCollapsedRegistrationGroups(new Set());
        setQuery((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: 1,
            },
        }));
    }, []);

    const showRegistrationGrouping =
        query.sort.field === AssetColumns._created_at &&
        query.sort.direction !== SortDirections.none;

    const hasCollapsedGroupsOnPage =
        showRegistrationGrouping &&
        paginatedRegistrationGroups.some((group) =>
            collapsedRegistrationGroups.has(group.key),
        );

    const visibleAssetRowIndexes = React.useMemo(() => {
        const visibleIndexes = new Map<number, number>();
        let visibleIndex = 0;

        paginatedAssets.forEach((asset, rawRowIndex) => {
            const groupKey = getRegistrationGroup(
                asset,
                selectedTableGrouping,
            ).key;
            const isHiddenByCollapse =
                showRegistrationGrouping &&
                collapsedRegistrationGroups.has(groupKey);

            if (isHiddenByCollapse) {
                return;
            }

            visibleIndexes.set(rawRowIndex, visibleIndex);
            visibleIndex += 1;
        });

        return visibleIndexes;
    }, [
        collapsedRegistrationGroups,
        paginatedAssets,
        selectedTableGrouping,
        showRegistrationGrouping,
    ]);

    const handleTableGroupingChange = React.useCallback(
        (updatedValue: IssueCountGroupingValue) => {
            Analytics.trackGroupingChange(updatedValue);
            setSelectedTableGrouping(updatedValue);
            setCollapsedRegistrationGroups(new Set());
            setQuery((prev) => ({
                ...prev,
                pagination: {
                    ...prev.pagination,
                    page: 1,
                },
                sort:
                    prev.sort.field === AssetColumns._created_at &&
                    prev.sort.direction !== SortDirections.none
                        ? prev.sort
                        : {
                              field: AssetColumns._created_at,
                              direction: SortDirections.desc,
                          },
            }));
        },
        [],
    );

    const toggleRegistrationGroup = React.useCallback((groupKey: string) => {
        setCollapsedRegistrationGroups((prev) => {
            const next = new Set(prev);

            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }

            return next;
        });
    }, []);

    // QUESTION - WTF is this code doing???
    React.useEffect(() => {
        const container = issueCountChartContainerRef.current;

        if (!container) {
            return;
        }

        const updateHeight = (nextHeight: number) => {
            if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
                return;
            }

            setIssueCountChartHeight((prev) => {
                return Math.abs(prev - nextHeight) > 2 ? nextHeight : prev;
            });
        };

        updateHeight(Math.floor(container.getBoundingClientRect().height));

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];

            updateHeight(Math.floor(entry?.contentRect.height ?? 0));
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <div className="space-y-4">
            {/* <VisualizationGrid className="gap-4">
                <VisualizationCard
                    title="New Issue Count"
                    infoTooltip="Uses the controls in this card. Main table filters below do not change this chart."
                    colSpans={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                        xxl: 12,
                    }}
                    wrapperClassNameOverride="flex grow min-h-0 w-full items-start"
                    containerClassNameOverride="flex grow min-h-0 flex-col"
                    showTimestamp={false}
                    customControls={
                        <div className="flex max-w-full flex-col items-start gap-2 sm:flex-row sm:items-center">
                            <div className="max-w-full overflow-x-auto">
                                <TabSelectV2
                                    title="Group By"
                                    options={issueCountGroupByTabOptions}
                                    value={selectedIssueCountGrouping}
                                    onValueChange={(value) => {
                                        setSelectedIssueCountGrouping(
                                            value as IssueCountGroupingValue,
                                        );
                                    }}
                                />
                            </div>

                            <VisualizationControls
                                legend={{
                                    value: showIssueCountLegend,
                                    toggle: () => {
                                        setShowIssueCountLegend(
                                            (prev) => !prev,
                                        );
                                    },
                                }}
                            />
                        </div>
                    }
                    cardStyle={{
                        minHeight: "360px",
                    }}
                >
                    <div
                        ref={issueCountChartContainerRef}
                        className="h-full min-h-0 w-full"
                    >
                        <TimeSeriesChart
                            series={issueCountSeries}
                            height={issueCountChartHeight}
                            area={false}
                            showTotal={true}
                            preserveSeriesOrder={true}
                            legend={{
                                enabled: showIssueCountLegend,
                                layout: "horizontal",
                                align: "center",
                                verticalAlign: "bottom",
                                truncateLabels: false,
                                // itemStyle: {
                                //     textAlign: "center",
                                //     textOverflow: "clip",
                                //     whiteSpace: "nowrap",
                                // },
                            }}
                            navigator={{
                                enabled: true,
                                active: true,
                                forceUpdate: false,
                            }}
                            yAxis={{
                                min: 0,
                                reversedStacks: true,
                            }}
                            columnPlotOptions={{
                                stacking: "normal",
                                pointPadding: 0.05,
                                groupPadding: 0.12,
                                borderRadius: 2,
                                dataGrouping: {
                                    enabled: false,
                                },
                            }}
                        />
                    </div>
                </VisualizationCard>
            </VisualizationGrid> */}

            <div className={cn("space-y-3")}>
                {/* <SectionHeader label="New Tokenized Asset Monitor" /> */}

                <div className="space-y-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-start">
                        <div className="flex min-w-0 w-full flex-col gap-1.5 lg:min-w-0 lg:flex-1">
                            <FilterLabel label="Search" />

                            <SelectLoginGate
                                isLoggedIn={isLoggedIn}
                                tooltipText="Log in to unlock search"
                            >
                                <Input
                                    value={searchQuery}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        Analytics.trackFilterSearch(value);
                                        setSearchQuery(value);
                                        resetToFirstPage();
                                    }}
                                    placeholder="Search"
                                    disabled={!isLoggedIn}
                                    className={cn(
                                        "w-full",
                                        CONTROL_INPUT_CLASS_NAME,
                                    )}
                                />
                            </SelectLoginGate>
                        </div>

                        <div className="w-full space-y-1 lg:mr-3 lg:w-[132px] lg:max-w-[132px] lg:shrink-0">
                            <FilterLabel label="Group By" />
                            <SelectLoginGate
                                isLoggedIn={isLoggedIn}
                                tooltipText="Log in to unlock group controls"
                            >
                                <select
                                    value={selectedTableGrouping}
                                    onChange={(event) => {
                                        handleTableGroupingChange(
                                            event.target
                                                .value as IssueCountGroupingValue,
                                        );
                                    }}
                                    disabled={!isLoggedIn}
                                    className={
                                        CONTROL_SELECT_TRIGGER_CLASS_NAME
                                    }
                                >
                                    {tableGroupByOptions.map((option) => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </SelectLoginGate>
                        </div>

                        <div className="w-full space-y-1 lg:mr-3 lg:w-[200px] lg:max-w-[200px] lg:shrink-0">
                            <FilterLabel label="Asset Class" />
                            <SelectLoginGate
                                isLoggedIn={isLoggedIn}
                                tooltipText="Log in to filter data"
                            >
                                <MultiSelect
                                    options={assetClassOptions}
                                    value={selectedAssetClasses}
                                    disabled={!isLoggedIn}
                                    onChange={(value) => {
                                        Analytics.trackFilterAssetClass(value);
                                        setSelectedAssetClasses(value);
                                        resetToFirstPage();
                                    }}
                                    placeholder="All classes"
                                    searchEnabled={true}
                                    searchPlaceholder="Search classes..."
                                    className="w-full"
                                    popoverWidth="w-[240px]"
                                    maxBadgesShown={1}
                                />
                            </SelectLoginGate>
                        </div>

                        <div className="w-full space-y-1 lg:mr-3 lg:w-[168px] lg:max-w-[168px] lg:shrink-0">
                            <FilterLabel label="Platform" />
                            <SelectLoginGate
                                isLoggedIn={isLoggedIn}
                                tooltipText="Log in to filter data"
                            >
                                <MultiSelect
                                    options={platformOptions}
                                    value={selectedPlatforms}
                                    disabled={!isLoggedIn}
                                    onChange={(value) => {
                                        Analytics.trackFilterPlatform(value);
                                        setSelectedPlatforms(value);
                                        resetToFirstPage();
                                    }}
                                    placeholder="All platforms"
                                    searchEnabled={true}
                                    searchPlaceholder="Search platforms..."
                                    className="w-full"
                                    popoverWidth="w-[240px]"
                                    maxBadgesShown={1}
                                />
                            </SelectLoginGate>
                        </div>

                        <div className="w-full space-y-1 lg:w-[168px] lg:max-w-[168px] lg:shrink-0">
                            <FilterLabel label="Network" />
                            <SelectLoginGate
                                isLoggedIn={isLoggedIn}
                                tooltipText="Log in to filter data"
                            >
                                <MultiSelect
                                    options={networkOptions}
                                    value={selectedNetworks}
                                    disabled={!isLoggedIn}
                                    onChange={(value) => {
                                        Analytics.trackFilterNetwork(value);
                                        setSelectedNetworks(value);
                                        resetToFirstPage();
                                    }}
                                    placeholder="All networks"
                                    searchEnabled={true}
                                    searchPlaceholder="Search networks..."
                                    className="w-full"
                                    popoverWidth="w-[240px]"
                                    maxBadgesShown={1}
                                />
                            </SelectLoginGate>
                        </div>

                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={activeFilterCount === 0}
                            className="h-9 w-full shrink-0 px-2.5 lg:ml-auto lg:w-auto select-none disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-100 disabled:hover:bg-slate-100"
                            onClick={() => {
                                Analytics.trackFilterClearAll({
                                    active_filter_count: activeFilterCount,
                                    source: "toolbar",
                                });
                                handleClearFilters();
                            }}
                            aria-label="Reset all table filters and search"
                        >
                            Reset filters
                        </Button>
                    </div>
                </div>

                {sortedAssets.length === 0 && (
                    <div className="flex flex-col items-center justify-center rounded-lg border bg-card px-6 py-16 text-center shadow-xs">
                        <p className="text-sm font-medium text-foreground">
                            No table rows match these controls
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Try broadening your search or clearing your filters.
                        </p>
                        {activeFilterCount > 0 && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="mt-4 px-2.5"
                                onClick={() => {
                                    Analytics.trackFilterClearAll({
                                        active_filter_count: activeFilterCount,
                                        source: "empty_state",
                                    });
                                    handleClearFilters();
                                }}
                            >
                                Reset table filters
                            </Button>
                        )}
                    </div>
                )}

                {sortedAssets.length > 0 && (
                    <DataGrid<AssetTableRowData, AssetColumns>
                        query={query}
                        onChangeQuery={setQuery}
                        rows={paginatedAssets as AssetTableRowData[]}
                        columns={TABLE_COLUMNS}
                        rowKey="id"
                        bordered
                        paginationState={paginationState}
                        analyticsTableName="new-asset-monitor-table"
                        size={RowSizes.small}
                        includeDummyRows={!hasCollapsedGroupsOnPage}
                    >
                        {({ row, rowIndex, scrollLeft }) => {
                            const asset = row as Asset;
                            const currentRegistrationGroup =
                                getRegistrationGroup(
                                    asset,
                                    selectedTableGrouping,
                                );
                            const previousRegistrationGroup =
                                rowIndex > 0
                                    ? getRegistrationGroup(
                                          paginatedAssets[
                                              rowIndex - 1
                                          ] as Asset,
                                          selectedTableGrouping,
                                      )
                                    : null;
                            const isFirstAssetInGroup =
                                showRegistrationGrouping &&
                                (rowIndex === 0 ||
                                    currentRegistrationGroup.key !==
                                        previousRegistrationGroup?.key);
                            const isCollapsed =
                                showRegistrationGrouping &&
                                collapsedRegistrationGroups.has(
                                    currentRegistrationGroup.key,
                                );
                            const visibleRowIndex =
                                visibleAssetRowIndexes.get(rowIndex) ??
                                rowIndex;

                            return (
                                <>
                                    {isFirstAssetInGroup && (
                                        <RegistrationGroupRow
                                            label={
                                                currentRegistrationGroup.label
                                            }
                                            itemCount={
                                                tableRegistrationGroupCounts.get(
                                                    currentRegistrationGroup.key,
                                                ) ?? 1
                                            }
                                            collapsed={isCollapsed}
                                            onToggle={() => {
                                                Analytics.trackIssuanceGroupToggle(
                                                    {
                                                        group_key:
                                                            currentRegistrationGroup.key,
                                                        group_label:
                                                            currentRegistrationGroup.label,
                                                        item_count:
                                                            tableRegistrationGroupCounts.get(
                                                                currentRegistrationGroup.key,
                                                            ) ?? 1,
                                                        action: isCollapsed
                                                            ? "expand"
                                                            : "collapse",
                                                    },
                                                );
                                                toggleRegistrationGroup(
                                                    currentRegistrationGroup.key,
                                                );
                                            }}
                                        />
                                    )}
                                    {!isCollapsed && (
                                        <AssetTableRow
                                            asset={{
                                                ...asset,
                                                tokens: sortItemsByProperty({
                                                    items: asset.tokens,
                                                    field: ASSET_SORT_MAP[
                                                        query.sort
                                                            .field as AssetColumns
                                                    ],
                                                    direction:
                                                        query.sort.direction,
                                                    nullsLast: true,
                                                }),
                                            }}
                                            size={RowSizes.small}
                                            columns={TABLE_COLUMNS}
                                            rowIndex={visibleRowIndex}
                                            scrollLeft={scrollLeft}
                                            bordered
                                            striped
                                            defaultExpandedState={false}
                                            includeAdditionalNetworksRow={false}
                                        />
                                    )}
                                </>
                            );
                        }}
                    </DataGrid>
                )}
            </div>
        </div>
    );
}
